/**
 * Leave Request Dialog Component
 *
 * Modal dialog for creating sick or vacation entries.
 * This is a simplified version of the VacationRequest page adapted for
 * use within the calendar interface. Lunch breaks are not managed here.
 *
 * @module Calendar/dialogs/LeaveRequestDialog
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Alert,
  CircularProgress,
  Typography,
} from '@mui/material';

const envVariable = require('../../../EnvironmentVariables.json');

/**
 * Leave Request Dialog
 *
 * Allows creation of leave/sick time for mechanics.
 *
 * @param {Object} props - Component props
 * @param {boolean} props.open - Controls dialog visibility
 * @param {Array<Object>} props.mechanics - List of available mechanics
 * @param {Function} props.onClose - Callback when dialog is closed
 * @param {Function} props.onSuccess - Callback when leave is successfully created
 * @returns {React.Element} Leave request dialog
 */
export const LeaveRequestDialog = ({ open, mechanics, appointments = [], onClose, onSuccess, onTaskConflict }) => {
  // Get today's date in YYYY-MM-DD format for date input default value
  const getTodayDateString = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [selectedDate, setSelectedDate] = useState(getTodayDateString());
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('17:00');
  const [selectedMechanic, setSelectedMechanic] = useState('');
  const [reason, setReason] = useState('sick');
  const [feedback, setFeedback] = useState(null);
  const [timeErr, setTimeErr] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e, skipConflictCheck = false) => {
    e.preventDefault();
    setLoading(true);
    setTimeErr(false);
    setFeedback(null);

    const startTimeHour = parseInt(startTime.substring(0, 2));
    const startTimeMinute = parseInt(startTime.substring(3, 5));
    const endTimeHour = parseInt(endTime.substring(0, 2));
    const endTimeMinute = parseInt(endTime.substring(3, 5));

    // Validate time format
    if (isNaN(startTimeHour) || isNaN(startTimeMinute) || isNaN(endTimeHour) || isNaN(endTimeMinute)) {
      setTimeErr(true);
      setLoading(false);
      return;
    }

    if (
      startTime[2] !== ':' ||
      endTime[2] !== ':' ||
      startTime.length !== 5 ||
      endTime.length !== 5 ||
      startTimeHour > 23 ||
      startTimeMinute > 59 ||
      endTimeHour > 23 ||
      endTimeMinute > 59
    ) {
      setTimeErr(true);
      setLoading(false);
      return;
    }

    // Ensure start time is before end time
    if (startTimeHour > endTimeHour || (startTimeHour === endTimeHour && startTimeMinute >= endTimeMinute)) {
      setTimeErr(true);
      setLoading(false);
      return;
    }

    // Build UTC datetime strings
    // User enters times in their local timezone, we convert to UTC for storage
    let startdatetime, enddatetime;
    try {
      const localStartDateTime = new Date(`${selectedDate}T${startTime}:00`);
      const localEndDateTime = new Date(`${selectedDate}T${endTime}:00`);

      // Validate that dates were parsed correctly
      if (isNaN(localStartDateTime.getTime()) || isNaN(localEndDateTime.getTime())) {
        setFeedback({ message: 'Invalid date or time. Please check your inputs and try again.', success: false });
        setLoading(false);
        return;
      }

      // UTC datetime strings for backend (both conflict checks and VacationRequest.php)
      startdatetime = localStartDateTime.toISOString().slice(0, 19).replace('T', ' ');
      enddatetime = localEndDateTime.toISOString().slice(0, 19).replace('T', ' ');

      console.log('[LeaveRequestDialog] Converting times to UTC:', {
        utcStart: startdatetime,
        utcEnd: enddatetime,
      });
    } catch (error) {
      console.error('[LeaveRequestDialog] Date parsing error:', error);
      setFeedback({ message: 'Invalid date or time. Please check your inputs and try again.', success: false });
      setLoading(false);
      return;
    }

    // Find mechanic ID from the mechanics list
    const selectedMechanicObject = mechanics.find(
      (mechanic) => mechanic.user_id === parseInt(selectedMechanic)
    );
    const mechanicid = selectedMechanicObject?.user_id;
    const mechanicName = selectedMechanicObject
      ? `${selectedMechanicObject.first_name} ${selectedMechanicObject.last_name}`
      : '';
    const SC_Id = localStorage.getItem('SC_Id');

    // Validate that mechanic was selected and found
    if (!mechanicid || !SC_Id) {
      console.error('[LeaveRequestDialog] Validation failed:', {
        mechanicid,
        SC_Id,
        selectedMechanic,
        mechanicsLength: mechanics.length
      });
      setFeedback({
        message: !mechanicid ? 'Please select a mechanic' : 'Service center ID not found. Please log in again.',
        success: false
      });
      setLoading(false);
      return;
    }

    // Check for overlapping leave blocks first (use UTC times)
    if (!skipConflictCheck) {
      try {
        const conflictResponse = await fetch(envVariable.serverId + '/SCO/checkBlockConflicts.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            mechanic_id: mechanicid,
            sc_id: SC_Id,
            start_date_time: startdatetime,
            end_date_time: enddatetime,
            block_type: 'leave',
            exclude_current: false,
          }),
        });

        const conflictResult = await conflictResponse.json();

        // Check if backend returned an error (e.g., missing parameters)
        if (conflictResult.type === 'error') {
          console.error('[LeaveRequestDialog] Conflict check error:', conflictResult);
          setFeedback({
            message: conflictResult.message || 'Error checking for conflicts',
            success: false
          });
          setLoading(false);
          return;
        }

        // Check for conflicts with other leave blocks
        if (conflictResult.leave_conflicts && conflictResult.leave_conflicts.length > 0) {
          setFeedback({
            message: 'This leave period conflicts with another leave period for this mechanic',
            success: false
          });
          setLoading(false);
          return;
        }
      } catch (error) {
        console.error('[LeaveRequestDialog] Error checking for conflicts:', error);
        // Continue anyway - don't block on conflict check failure
      }
    }

    // Check for task conflicts unless we're skipping the check (already confirmed)
    console.log('[LeaveRequestDialog] Conflict check:', {
      skipConflictCheck,
      hasAppointments: !!appointments,
      appointmentsLength: appointments?.length || 0,
      mechanicid,
    });

    if (!skipConflictCheck && appointments && appointments.length > 0) {
      // Parse UTC times for comparison with task times
      const startWithZ = startdatetime.includes('T') ? startdatetime : startdatetime.replace(' ', 'T');
      const endWithZ = enddatetime.includes('T') ? enddatetime : enddatetime.replace(' ', 'T');
      const leaveStart = new Date(startWithZ.endsWith('Z') ? startWithZ : startWithZ + 'Z');
      const leaveEnd = new Date(endWithZ.endsWith('Z') ? endWithZ : endWithZ + 'Z');

      console.log('[LeaveRequestDialog] Checking for conflicts in date range:', {
        leaveStart: leaveStart.toISOString(),
        leaveEnd: leaveEnd.toISOString(),
        totalAppointments: appointments.length,
      });

      const conflictingTasks = appointments.filter((task) => {
        // Only check tasks assigned to this mechanic
        if ((task.mechanic_id || task.mechanic) !== mechanicid) return false;

        // Task dates from Calendar.js are already parsed as Date objects
        const taskStart = new Date(task.startDate || task.calendar_start || task.appointment_time);
        const taskEnd = new Date(task.endDate || task.calendar_end);

        // Check if task overlaps with leave time
        return (
          (taskStart >= leaveStart && taskStart < leaveEnd) || // Task starts during leave
          (taskEnd > leaveStart && taskEnd <= leaveEnd) || // Task ends during leave
          (taskStart <= leaveStart && taskEnd >= leaveEnd) // Task spans entire leave
        );
      });

      console.log('[LeaveRequestDialog] Found conflicting tasks:', conflictingTasks.length, conflictingTasks);

      if (conflictingTasks.length > 0) {
        console.log('🔍 Showing conflict dialog for tasks:', conflictingTasks);
        setLoading(false);
        // Call the onTaskConflict callback to show the conflict dialog
        if (onTaskConflict) {
          onTaskConflict({
            conflictingTasks,
            mechanicName,
            mechanicId: mechanicid,
            leaveType: reason,
            leaveData: {
              startdatetime,
              enddatetime,
              mechanicid,
              mechanicName,
              reason,
              sc_id: SC_Id,
            },
          });
        }
        return;
      }
    } else {
      console.log('[LeaveRequestDialog] Skipping frontend conflict check - will rely on backend');
    }

    try {
      const response = await fetch(envVariable.serverId + '/SCO/VacationRequest.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'create',
          start_date_time: startdatetime,
          end_date_time: enddatetime,
          mechanic_id: mechanicid,
          reason,
          sc_id: SC_Id,
          status: 'approved', // Calendar-created leaves are approved by default
          title: reason === 'sick' ? 'Sick Leave' : 'Vacation',
        }),
      });

      const result = await response.json();

      if (result.status === 'Success') {
        setFeedback({ message: result.message, success: true });
        // Call onSuccess after a short delay to show the success message
        setTimeout(async () => {
          if (onSuccess) {
            onSuccess(); // Triggers refresh
            // Wait for refresh to complete
            await new Promise(resolve => setTimeout(resolve, 800));
          }
          handleClose();
        }, 800);
      } else {
        // Check if this is a task conflict error from the backend
        if (result.requires_unassignment && result.conflicting_tasks) {
          console.log('[LeaveRequestDialog] Backend detected task conflicts:', result.conflicting_tasks);
          setLoading(false);
          // Show the conflict dialog so user can confirm unassignment
          if (onTaskConflict) {
            onTaskConflict({
              conflictingTasks: result.conflicting_tasks,
              mechanicName,
              mechanicId: mechanicid,
              leaveType: reason,
              leaveData: {
                startdatetime,
                enddatetime,
                mechanicid,
                mechanicName,
                reason,
                sc_id: SC_Id,
              },
            });
          }
          return;
        }

        setFeedback({ message: result.message, success: false });
      }
    } catch (error) {
      console.error('Error submitting leave request:', error);
      setFeedback({
        message: 'Unexpected error occurred. Please try again.',
        success: false,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Reset form
    setSelectedDate(getTodayDateString());
    setStartTime('09:00');
    setEndTime('17:00');
    setSelectedMechanic('');
    setReason('sick');
    setFeedback(null);
    setTimeErr(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Vacation/Sick Time</DialogTitle>

      <DialogContent>
        <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {/* Date Picker */}
          <TextField
            fullWidth
            label="Select Date"
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            required
          />

          {/* Start Time */}
          <TextField
            fullWidth
            label="Start Time"
            type="time"
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
            InputLabelProps={{ shrink: true }}
            required
          />

          {/* End Time */}
          <TextField
            fullWidth
            label="End Time"
            type="time"
            value={endTime}
            onChange={(e) => setEndTime(e.target.value)}
            InputLabelProps={{ shrink: true }}
            required
          />
          {timeErr && (
            <Typography color="error" variant="caption">
              Invalid time. Ensure start time is earlier than end time.
            </Typography>
          )}

          {/* Mechanic Selector */}
          <FormControl fullWidth required>
            <InputLabel>Select Mechanic</InputLabel>
            <Select
              label="Select Mechanic"
              value={selectedMechanic}
              onChange={(e) => setSelectedMechanic(e.target.value)}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {mechanics.map((mechanic) => (
                <MenuItem key={mechanic.user_id} value={mechanic.user_id}>
                  {mechanic.first_name} {mechanic.last_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Reason Selector */}
          <FormControl fullWidth required>
            <InputLabel>Reason</InputLabel>
            <Select label="Reason" value={reason} onChange={(e) => setReason(e.target.value)}>
              <MenuItem value="sick">Sick</MenuItem>
              <MenuItem value="vacation">Vacation</MenuItem>
            </Select>
          </FormControl>

          {/* Feedback Message */}
          {feedback && (
            <Alert severity={feedback.success ? 'success' : 'error'}>{feedback.message}</Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} variant="outlined">
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" color="primary" disabled={loading}>
          {loading ? <CircularProgress size={24} /> : 'Submit'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
